import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  IconButton,
  IconButtonProps,
  PlacementWithLogical,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverProps,
  PopoverTrigger,
  Stack,
  StackProps,
  Text,
  TextProps,
  Tooltip,
  VisuallyHidden,
  useMergeRefs,
} from '@chakra-ui/react';
import styled, { CSSObject } from '@emotion/styled';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron_down.svg';
import { Code, TagName } from 'constants/keyboard';
import { KeyboardEvent, MouseEvent, ReactElement, ReactNode, Ref, useRef } from 'react';

import { PopoverWrapper } from '../PopoverWrapper';
import { DropdownPopoverOption, DropdownPopoverOptionProps } from './DropdownPopoverOption';

const StyledCheckIcon = styled(CheckIcon)`
  & path {
    fill: none !important;
  }
`;

export type DropdownPopoverOptions = Array<DropdownPopoverOptionProps>;

interface TextButtonProps {
  textButtonProps?: TextProps;
}

export interface DropdownPopoverProps<T = string> extends Partial<PopoverProps & TextButtonProps> {
  /**
   * The width of the trigger button and its popover content
   */
  w?: string | number;

  /**
   * The height of the popover content
   */
  h?: string | number;

  /**
   * The max height of the popover content
   */
  maxH?: string | number;

  /**
   * The min height of the popover content
   */
  minW?: string | number;

  /**
   * The max width of the popover content
   */
  maxW?: string | number;

  /**
   * The root container props
   */
  containerProps?: BoxProps & {
    [key: `data-${string}`]: any;
  };

  /**
   * The props applied to the trigger button
   */
  triggerProps?: Partial<ButtonProps & IconButtonProps> & {
    ref?: Ref<HTMLButtonElement>;
    [key: `data-${string}`]: any;
  };

  /**
   * Spacing between the options within the popuver
   */
  spacing?: StackProps['spacing'];

  selectedIndex?: number;

  /**
   * Possible options of this selector
   */
  options: DropdownPopoverOptions;

  /**
   * The optionProps is the props of each option within the popover
   * Each option within the popover is a button, which would contain a span component
   * consuming all porps defined in the optionProps
   * @see DropdownPopoverOptionProps
   */
  optionProps?: BoxProps;

  justifyContent?: string;
  toolbarIcon?: ReactElement;

  /**
   * The value displayed on the trigger button
   */
  label: string;

  onSelect?: (value: T, index: number) => void;
  showSelectedIcon?: boolean;
  selectedIcon?: ReactElement; // icon to display for the selected option
  header?: ReactNode;
  placement?: PlacementWithLogical;
  buttonVariant?: string;
  focusShadow?: CSSObject;
  optionsButtonVariant?: string;
  toolbarIconVariant?: string;
  buttonOptionProps?: ButtonProps;
  triggerSelectOnSameValue?: boolean; // Call onSelect even if we click on the already selected value
  closeOnSelect?: boolean; // Close dropdown after select
  ariaLabel?: string;

  /**
   * If the dropdown trigger button has a value it is used as the hover tooltip label.
   * The number steps in Map Widget needs to display the label: "Steps" instead of the numeric value like "5".
   */
  useLabelInsteadOfSelectedLabel?: boolean;

  /**
   * Content overflow can be undefined, fixes for tooltip clipped.
   */
  contentOverflowY?: 'visible' | 'auto';
  // Optional class name for the popover content
  contentClassName?: string;

  /**
   * Number in px
   */
  headerBorderBottomWidth?: number;

  children?: ReactElement | ReactNode;

  /**
   * Disable tooltip on the trigger button
   */
  isTooltipDisabled?: boolean;

  /**
   * Disable the dropdown trigger button
   */
  isDisabled?: boolean;

  /**
   * Text label for the trigger button that will never change
   */
  staticLabel?: string;
}

/**
 * DROPDOWN POPOVER COMPONENT
 *
 * This component renders a Chakra icon button that when clicked will
 * display a Chakra Popover component with dropdown options specified by
 * 'options' prop.
 *
 * If no icon is defined, a text button will render as the trigger.
 *
 * TODO: This component is missing the accessibility control.
 * we need to block some keyboard events from popping up to the parent of the component.
 */
export const DropdownPopover = <T extends string>({
  containerProps,
  selectedIndex,
  options,
  toolbarIcon,
  label,
  header,
  selectedIcon,
  placement,
  buttonVariant,
  optionsButtonVariant,
  toolbarIconVariant = 'icon-btn-toolbar-option',
  optionProps,
  justifyContent,
  onSelect = () => {},
  w = '8.5rem',
  h,
  maxH,
  minW,
  maxW,
  focusShadow = { boxShadow: 'outline' },
  buttonOptionProps = {},
  showSelectedIcon = false,
  triggerProps = {},
  triggerSelectOnSameValue = true,
  closeOnSelect = false,
  spacing,
  useLabelInsteadOfSelectedLabel = false,
  contentOverflowY = 'auto',
  contentClassName,
  headerBorderBottomWidth = 1,
  ariaLabel,
  staticLabel,
  children,
  textButtonProps,
  isDisabled,
  isTooltipDisabled,
  ...popoverProps
}: DropdownPopoverProps<T>) => {
  const { ref: triggerRefProp, ...otherTriggerProps } = triggerProps;

  // The ref of the trigger button that is created internally.
  const internalTriggerRef = useRef<HTMLButtonElement>(null);

  // triggerRef is used for manually focusing the trigger button when the dropdown is closed by a keyboard event
  const triggerRef = useMergeRefs(internalTriggerRef, triggerRefProp);

  // The ref of the first selectable option button.
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  // A flag to determine if the user use keyboard to select an option.
  // We have different styles for keyboard (Space or Enter) and mouse selection (click).
  // When we detect a Enter or Space key down within a selectable option, this will be set to true.
  // While this is true, and when the dropdown is close, we will set the focus to the dropdown trigger by js.
  const isKeyboardSelectEvent = useRef(false);

  const handleSelect = (e: MouseEvent<HTMLButtonElement>): void => {
    const value = e.currentTarget.dataset.value as unknown as T;

    // Don't call onSelect for non-changed values.
    if (!triggerSelectOnSameValue && selectedIndex === options.findIndex((option) => option.value === value)) {
      return;
    }

    onSelect(value, Number(e.currentTarget.dataset.index));
  };

  const selectedLabel = selectedIndex !== undefined ? options[selectedIndex]?.label : undefined;

  const handleOptionKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === Code.Tab && e.shiftKey && e.target === firstOptionRef.current) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.parentElement?.focus();
    }

    const targetElement = e.target as HTMLElement;
    if (closeOnSelect && targetElement.tagName === TagName.Button && (e.key === Code.Enter || e.key === Code.Space)) {
      isKeyboardSelectEvent.current = true;
    }
  };

  const optionalAriaLabelProp =
    ariaLabel !== undefined ? { 'aria-label': ariaLabel?.replace('{{selectedLabel}}', selectedLabel || label) } : {};

  return (
    <PopoverWrapper {...containerProps}>
      <Popover placement={placement || 'bottom-start'} closeOnBlur closeOnEsc isLazy {...popoverProps}>
        {({ isOpen, onClose }) => {
          const handleCloseOnSelect = (e: MouseEvent<HTMLDivElement>) => {
            if (!closeOnSelect) return;

            e.preventDefault();
            e.stopPropagation();
            onClose();

            if (isKeyboardSelectEvent.current) {
              internalTriggerRef.current?.focus();
              isKeyboardSelectEvent.current = false;
            }
          };

          return (
            <>
              <Tooltip
                hasArrow
                placement={'bottom'}
                label={useLabelInsteadOfSelectedLabel ? label : selectedLabel ?? label}
                bg='black'
                isDisabled={isDisabled || isTooltipDisabled}
              >
                <Box overflow='inherit'>
                  <PopoverTrigger>
                    {toolbarIcon ? (
                      <IconButton
                        variant={toolbarIconVariant}
                        isActive={isOpen}
                        aria-label={label}
                        icon={toolbarIcon}
                        size='sm'
                        ref={triggerRef}
                        {...otherTriggerProps}
                      />
                    ) : (
                      <Button
                        leftIcon={selectedIndex !== undefined ? options[selectedIndex]?.icon : undefined}
                        variant={buttonVariant || 'outline'}
                        rightIcon={<ChevronDownIcon />}
                        isActive={isOpen}
                        isDisabled={isDisabled}
                        size={'xs'}
                        width={w}
                        _focus={focusShadow}
                        ref={triggerRef}
                        {...optionalAriaLabelProp}
                        {...otherTriggerProps}
                      >
                        <Text fontSize={'sm'} fontWeight={'500'} flexGrow='1' textAlign='left' {...textButtonProps}>
                          {staticLabel || selectedLabel || label}
                        </Text>
                      </Button>
                    )}
                  </PopoverTrigger>
                </Box>
              </Tooltip>
              <PopoverContent
                w={w}
                h={h}
                maxH={maxH}
                minW={minW}
                maxW={maxW}
                overflowY={contentOverflowY}
                boxShadow='md'
                padding='0'
                data-testid='popover-content'
                rootProps={{ zIndex: 'dropdownPanel' }}
                className={contentClassName}
              >
                {header ? (
                  <PopoverHeader p={2} borderBottomWidth={headerBorderBottomWidth}>
                    {header}
                  </PopoverHeader>
                ) : (
                  <VisuallyHidden>
                    <PopoverHeader>{label}</PopoverHeader>
                  </VisuallyHidden>
                )}
                <Stack p={2} onClick={handleCloseOnSelect} onKeyDown={handleOptionKeyDown} spacing={spacing}>
                  {options.map((opt, i) => {
                    if (opt.isDivider) return <Divider key={`dropdown-option-${i}-divider`} />;
                    if (opt.isSubHeader)
                      return (
                        <Text
                          key={`dropdown-option-${i}-sub-header`}
                          fontSize={'xs'}
                          fontWeight={'300'}
                          color={'font.400'}
                        >
                          {opt.label}
                        </Text>
                      );

                    const isSelected = i === selectedIndex;

                    return (
                      <DropdownPopoverOption
                        key={`dropdown-option-${i}-${opt.value}`}
                        ref={i === 0 ? firstOptionRef : undefined}
                        icon={opt.icon}
                        index={i}
                        label={opt.label}
                        value={opt.value}
                        isActive={isSelected}
                        aria-selected={isSelected}
                        onClick={handleSelect}
                        optionProps={{ ...optionProps, ...opt.optionProps }}
                        hoveredOptionProps={{ ...optionProps, ...opt.hoveredOptionProps }}
                        hoveredlabel={opt.hoveredlabel}
                        hoveredTooltip={opt.hoveredTooltip}
                        hoveredIcon={opt.hoveredIcon}
                        isDisabled={opt.isDisabled}
                        variant={optionsButtonVariant || 'toolbar-dropdown-item'}
                        rightIcon={
                          opt.rightIcon
                            ? opt.rightIcon
                            : showSelectedIcon && isSelected
                            ? selectedIcon || <StyledCheckIcon />
                            : undefined
                        }
                        {...buttonOptionProps}
                      />
                    );
                  })}
                </Stack>
                {children && (
                  <Stack p={2} onClick={handleCloseOnSelect} onKeyDown={handleOptionKeyDown} spacing={spacing}>
                    {children}
                  </Stack>
                )}
              </PopoverContent>
            </>
          );
        }}
      </Popover>
    </PopoverWrapper>
  );
};
